import { pathOr } from 'ramda';
import type { ShopData } from '../components/ShopText';
import type { ContentAggregate } from '../models.generated';
import { useContentAggregateQuery } from '../queries/contentTypes/contentAggregate.generated';
import type { HeaderBodyImageWithLinkItem } from './useHeaderBodyImageWithLinkData';
import { transformHeaderBodyImageWithLinkData } from './useHeaderBodyImageWithLinkData';
import type { LongHeaderBodyFootnoteData } from './useLongHeaderBodyFootnoteData';
import { toLongHeaderBodyFootnoteData } from './useLongHeaderBodyFootnoteData';
import type { ProductBlockData } from './useProductBlockData';
import { transformProductBlockData } from './useProductBlockData';
import type { ProductBlockSetData } from './useProductBlockSetData';
import { transformProductBlockSetData } from './useProductBlockSetData';
import type { PromotionalTextData } from './usePromotionalTextData';

export type ContentAggregateData = {
  key: string;
  __typename: string;
};

export type ProductBlockAggregateData = {
  key: string;
  data: ProductBlockData;
  __typename: string;
};

export type ProductBlockSetAggregateData = {
  key: string;
  data: ProductBlockSetData;
  __typename: string;
};

export type LongHeaderBodyFootnoteAggregateData = {
  key: string;
  data: LongHeaderBodyFootnoteData;
  __typename: string;
};

export const transformContentAggregateData = (
  response: object,
): ContentAggregateData[] => {
  const collection = pathOr(
    [],
    ['data', 'contentAggregate', 'aggregateCollection', 'items'],
    response,
  );
  const transformedCollection = collection.map(
    (item: ContentAggregateData & Record<string, unknown>) => {
      switch (item['__typename']) {
        case 'ProductBlockSet':
          return {
            key: item['key'],
            data: transformProductBlockSetData({ data: { productBlockSet: item } }),
            __typename: 'ProductBlockSet',
          } as ProductBlockSetAggregateData;
        case 'ProductBlock':
          return {
            key: item['key'],
            data: transformProductBlockData({ data: { productBlock: item } }),
            __typename: 'ProductBlock',
          } as ProductBlockAggregateData;
        case 'HeaderBodyImageWithLink':
          return transformHeaderBodyImageWithLinkData({
            data: { headerBodyImageWithLink: item },
          });
        case 'LongHeaderBodyFootnote':
          return toLongHeaderBodyFootnoteData(item);
        default:
          return item;
      }
    },
  );

  return transformedCollection;
};

const useContentAggregateData = (
  id: ContentAggregate,
): (
  | ContentAggregateData
  | ProductBlockAggregateData
  | ProductBlockSetAggregateData
  | PromotionalTextData
  | ShopData
  | HeaderBodyImageWithLinkItem
  | LongHeaderBodyFootnoteAggregateData
)[] => {
  const res = useContentAggregateQuery({ variables: { id } });

  return transformContentAggregateData(res);
};

export default useContentAggregateData;
